import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, TemplateRef, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppService } from '../../../../services/app.service';
import { ActivityService } from '../../../../services/activity.service';
import { SocketService } from '../../../../services/socket.service';
import { SoftphoneService } from '../../../../services/softphone.service';
import type { SoftphoneSessionActiveCallSummary } from '../../../../services/softphone-session-coordinator.service';
import { normalizePhone, SoftphoneParticipantSnapshotDto, TransferActivity } from 'voxr-shared';
import { UserAvatarComponent } from '../../../user/user-avatar/user-avatar.component';
import type { Call, Device } from '@twilio/voice-sdk';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import type { SoftphoneParticipantRow } from '../../../../services/softphone-participants.mapper';
import { SoftphoneConferenceSession } from '../../../../services/softphone-conference-session';
import { SoftphoneDiagnostics } from '../../../../services/softphone-diagnostics';
import { SoftphoneExceptionService } from '../../../../services/softphone-exception.service';

type CoachState = 'idle' | 'joining' | 'active' | 'error';
type CoachMode = 'whisper' | 'barge';
type CoachActionKind = 'join' | 'call' | 'mute' | 'mode' | 'three-way' | 'participant' | 'conference';
@Component({
  selector: 'app-transfer-coach',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatTooltipModule,
    UserAvatarComponent,
  ],
  templateUrl: './transfer-coach.component.html',
})
export class TransferCoachComponent implements AfterViewInit, OnDestroy {
  private diagnostics = new SoftphoneDiagnostics();
  @Input() transfer!: TransferActivity;
  @Output() left = new EventEmitter<void>();
  @ViewChild('coachDialog', { static: true }) coachDialog!: TemplateRef<unknown>;
  @ViewChild('endConferenceConfirmation', { static: true }) endConferenceConfirmation!: TemplateRef<unknown>;

  private activityService = inject(ActivityService);
  private softphoneService = inject(SoftphoneService);
  private exceptions = inject(SoftphoneExceptionService);
  private socketService = inject(SocketService);
  private app = inject(AppService);
  private dialog = inject(MatDialog);
  private readonly threeWayNumberStorageKey = 'voxr.live-transfers.three-way-number';

  state = signal<CoachState>('idle');
  stateDetail = signal('');
  muted = signal(true);
  coachMode = signal<CoachMode>('whisper');
  switchingMode = signal(false);
  togglingMute = signal(false);
  placingThreeWay = signal(false);
  threeWayAccepted = signal(false);
  threeWayConnectionRecovering = signal(false);
  threeWayNumber = signal('');
  coachCallSid = signal<string | undefined>(undefined);

  private reconnecting = false;
  private device?: Device;
  private activeCall?: Call;
  private consumerId?: string;
  private publishedActiveCall?: SoftphoneSessionActiveCallSummary & { callId: string; integrationId: string; };
  private joinedAt?: number;
  private uiTimer?: ReturnType<typeof setInterval>;
  private leaveRequested = false;
  private dialogRef?: MatDialogRef<unknown>;
  private isClosing = false;
  private pendingIncomingCall?: Call;
  private mainLegStartedAt = signal<number | undefined>(undefined);
  private thirdLegStartedAt = signal<number | undefined>(undefined);
  private activeCallVoxrId = signal<string | undefined>(undefined);
  private readonly conferenceSession = new SoftphoneConferenceSession(this.softphoneService, {
    context: () => ({
      callId: this.getActiveCallIdForParticipantOps(),
      activityId: this.transfer?._id?.toString(),
      agentCallSid: this.activeCall?.parameters?.['CallSid'],
    }),
    rowsApplied: rows => this.applyConferenceTiming(rows),
  });
  coachDurationSecs = signal(0);
  mainDurationSecs = signal(0);
  thirdDurationSecs = signal(0);
  participantRows = this.conferenceSession.rows;

  readonly hasOngoingCall = computed(() => !!this.activeCall || this.state() === 'active' || this.state() === 'joining');
  readonly mainParticipantLeg = computed(() => this.participantRows().find(row => row.legType === 'main'));
  readonly thirdParticipantLeg = this.conferenceSession.thirdParticipantLeg;
  readonly thirdParticipantEnded = this.conferenceSession.thirdParticipantEnded;
  readonly hasMainParticipantLeg = computed(() => !!this.mainParticipantLeg()?.callSid);
  readonly hasThirdParticipantLeg = computed(() => !!this.thirdParticipantLeg()?.callSid);
  readonly hasConferenceParticipants = computed(() => this.participantRows().length > 0);
  readonly conferenceMutationPending = computed(() =>
    this.placingThreeWay()
    || this.conferenceSession.mutationPending()
    || this.switchingMode()
    || this.togglingMute()
  );
  readonly mainParticipantStatusLabel = computed(() => this.mainParticipantLeg()?.status || '--');
  readonly threeWayReadinessReason = computed(() => {
    if (this.state() !== 'active' || !this.activeCall) return 'call-not-connected';
    if (!this.activeCall.parameters?.['CallSid']) return 'coach-leg-unavailable';
    if (this.threeWayConnectionRecovering()) return 'connection-recovering';
    if (this.threeWayAccepted() || this.hasThirdParticipantLeg()) return 'third-party-exists';
    if (normalizePhone(this.threeWayNumber()).error) return 'number-required';
    if (this.conferenceMutationPending()) return 'mutation-pending';
    return 'ready';
  });
  readonly canStartThreeWay = computed(() => this.threeWayReadinessReason() === 'ready');
  readonly thirdParticipantStatusLabel = computed(() => {
    const third = this.thirdParticipantLeg();
    if (this.thirdParticipantEnded()) return 'Ended';
    if (third?.status) return third.status;
    if (this.placingThreeWay()) return 'ringing';
    return '--';
  });
  readonly coachTimerText = computed(() => this.formatDuration(this.coachDurationSecs()));
  readonly mainParticipantTimerText = computed(() => this.formatDuration(this.mainDurationSecs()));
  readonly thirdParticipantTimerText = computed(() => {
    const third = this.thirdParticipantLeg();
    if (this.thirdParticipantEnded()) {
      return third?.startedAt && third.endedAt
        ? this.formatDuration(Math.max(0, Math.floor((third.endedAt - third.startedAt) / 1000)))
        : '--:--';
    }
    return this.formatDuration(this.thirdDurationSecs());
  });
  readonly loadingMessage = computed(() => {
    if (this.placingThreeWay()) return 'Starting three-way call...';
    if (this.state() === 'joining') return 'Joining coaching session...';
    if (this.state() === 'error') return this.stateDetail();
    return undefined;
  });
  readonly loadingState = computed(() => {
    const message = this.loadingMessage();
    return {
      active: !!message || this.conferenceSession.loading() || this.conferenceMutationPending(),
      message,
      showProgressBar: this.state() === 'joining' || this.placingThreeWay() || this.conferenceSession.loading() || this.conferenceSession.ending(),
      pendingParticipantCallSid: this.conferenceSession.pendingParticipantCallSid(),
      endingConference: this.conferenceSession.ending(),
    };
  });

  constructor() {
    this.softphoneService.deviceEvent$.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event.device !== this.device) return;
      if (event.kind === 'registered') this.onDeviceRegistered();
      else if (event.kind === 'unregistered') void this.onDeviceUnregistered();
      else this.onDeviceError(event.error);
    });
    this.softphoneService.coachIncomingCall$.pipe(takeUntilDestroyed()).subscribe(({ call, consumerId }) => {
      if (!this.consumerId || consumerId !== this.consumerId) return;
      this.onDeviceIncoming(call);
    });
    toObservable(this.threeWayReadinessReason).pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe(reason => {
      this.logThreeWay('three-way:readiness', {
        reason,
        ready: reason === 'ready',
        reconnecting: this.threeWayConnectionRecovering(),
        hasTransferContext: !!this.transfer?._id,
        callIdAvailable: !!this.transfer?.callId || !!this.activeCallVoxrId(),
        callSidAvailable: !!this.activeCall?.parameters?.['CallSid'],
        mutationPending: this.conferenceMutationPending(),
      });
    });
    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string }>('softphone-participants-updated')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.reconcileParticipantSnapshot(payload);
      });

    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string; participantCallSid?: string; action?: string }>('softphone-participant-action')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.reconcileParticipantSnapshot(payload);
      });

    this.socketService.onEvent$<{ callId?: string; isOnHold?: boolean; participantCallSid?: string }>('softphone-hold-state')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        if (typeof payload?.isOnHold !== 'boolean' || !payload.participantCallSid) return;

        this.conferenceSession.applyHold(payload.participantCallSid, payload.isOnHold);
      });

    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string }>('softphone-participant-ended')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.reconcileParticipantSnapshot(payload);
      });

    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string }>('softphone-conference-ended')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.resetParticipantState();
      });
    this.socketService.onEvent$<{ campaignId: string }>('coaching-conference-refresh')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        if (payload?.campaignId !== this.transfer?.campaignId || this.state() !== 'active') return;
        void this.refreshParticipants();
      });
  }

  async ngAfterViewInit() {
    this.openDialog();
    this.threeWayNumber.set(localStorage.getItem(this.threeWayNumberStorageKey) || '');
    await this.joinSession();
    this.startUiTimer();
  }

  ngOnDestroy() {
    this.isClosing = true;
    this.conferenceSession.dispose();
    this.dialogRef?.close();
    this.stopUiTimer();
    void this.requestLeaveIfJoined();
    this.destroyDevice();
  }

  private openDialog() {
    this.dialogRef = this.dialog.open(this.coachDialog, {
      width: '420px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      restoreFocus: false,
    });

    this.dialogRef.afterClosed().subscribe(() => {
      if (this.isClosing) return;
      void this.closeSession();
    });
  }

  async joinSession() {
      this.state.set('joining');
      this.stateDetail.set('Initializing...');
    try {
      this.consumerId = `transfer-coach:${this.transfer?._id?.toString() || Date.now()}`;
      this.softphoneService.beginCoachIncomingSession(this.consumerId, {
        transferId: this.transfer._id?.toString(),
        callId: this.transfer.callId?.toString(),
        integrationId: this.transfer.integrationId,
      });
      this.device = await this.softphoneService.acquireSoftphoneDevice(this.consumerId, {
        ensureMicrophone: true,
        integrationId: this.transfer.integrationId,
      });
      if (!this.device) throw new Error('Transfer coaching is controlled by the active soft phone owner tab.');

      this.softphoneService.recordCoachCorrelationStage('device-acquired', { consumerId: this.consumerId });
      this.softphoneService.recordCoachCorrelationStage('request-started', { consumerId: this.consumerId });

      const { coachCallSid } = await this.activityService.coachingJoin(this.transfer._id!.toString());
      this.coachCallSid.set(coachCallSid);
      this.softphoneService.setCoachIncomingCallSid(this.consumerId, coachCallSid);
      this.tryAttachPendingCoachCall();
      this.stateDetail.set('Waiting for connection...');
      this.muted.set(true);
      this.coachMode.set(this.transfer.transferMode?.coachMode || 'whisper');
      this.updateStateDetail();
    } catch (error: any) {
      this.recordException(error, 'join');
      if (this.consumerId) this.softphoneService.recordCoachCorrelationStage('request-failed', { consumerId: this.consumerId });
      await this.releaseCoachRuntime();
      this.device = undefined;
      this.state.set('error');
      this.stateDetail.set(this.getFriendlyActionError(error, 'join'));
      this.app.message$.next({ severity: 'danger', summary: 'Coach', detail: this.stateDetail() });
    }
  }

  private onDeviceRegistered() {
    if (this.state() !== 'joining') return;
    if (this.activeCall) {
      this.state.set('active');
      this.updateStateDetail();
      return;
    }
    this.stateDetail.set('Waiting for connection...');
  }

  private async onDeviceUnregistered() {
    if (this.isClosing || this.reconnecting || !this.consumerId) return;
    this.reconnecting = true;
    this.state.set('joining');
    this.stateDetail.set('Reconnecting soft phone session...');
    try {
      this.device = await this.softphoneService.recoverSoftphoneDevice(this.consumerId, {
        ensureMicrophone: true,
        integrationId: this.transfer.integrationId,
      });
      if (!this.device) throw new Error('Transfer coaching is controlled by the active soft phone owner tab.');
      if (this.activeCall) {
        this.state.set('active');
        this.updateStateDetail();
      } else {
        this.stateDetail.set('Waiting for connection...');
      }
    } catch (error: any) {
      this.recordException(error, 'reconnect');
      this.state.set('error');
      this.stateDetail.set('Soft phone could not reconnect. Try reopening the coaching session.');
      this.logThreeWay('device:reconnect-failed', { error: this.summarizeError(error) });
    } finally {
      this.reconnecting = false;
    }
  }

  private onDeviceIncoming = (call: Call) => {
    const incomingSid = call?.parameters?.['CallSid'];
    if (!this.coachCallSid()) {
      this.pendingIncomingCall = call;
      return;
    }
    if (incomingSid && incomingSid !== this.coachCallSid()) return;

    this.acceptCoachCall(call);
  };

  private tryAttachPendingCoachCall() {
    if (!this.pendingIncomingCall || !this.coachCallSid()) return;
    const pendingSid = this.pendingIncomingCall?.parameters?.['CallSid'];
    if (!pendingSid || pendingSid === this.coachCallSid()) {
      this.acceptCoachCall(this.pendingIncomingCall);
    }
    this.pendingIncomingCall = undefined;
  }

  private acceptCoachCall(call: Call) {
    this.bindCallEvents(call);
    call.accept();
    this.activeCall = call;
    this.state.set('active');
    this.joinedAt = Date.now();
    this.coachDurationSecs.set(0);
    this.mainLegStartedAt.set(Date.now());
    this.resolveAndSetCallId(call);
    this.publishCoachActiveCall(call);
    void this.refreshParticipants();
    this.stateDetail.set('Listening silently');
  }

  private onDeviceError = (error: any) => {
    this.recordException(error, 'device-callback');
    this.state.set('joining');
    this.stateDetail.set('Recovering soft phone connection...');
    this.logThreeWay('device:error', { error: this.summarizeError(error) });
  };

  private bindCallEvents(call: Call) {
    call.on('disconnect', () => {
      this.activeCall = undefined;
      this.threeWayConnectionRecovering.set(false);
      this.threeWayAccepted.set(false);
      void this.releaseCoachRuntime();
      void this.requestLeaveIfJoined();
      this.stateDetail.set('Call ended.');
      this.state.set('idle');
      this.joinedAt = undefined;
      this.coachDurationSecs.set(0);
      this.resetParticipantState();
    });
    call.on('error', (error: any) => {
      this.recordException(error, 'call-callback');
      this.state.set('error');
      this.stateDetail.set(this.getFriendlyActionError(error, 'call'));
      this.app.message$.next({ severity: 'danger', summary: 'Coach', detail: this.stateDetail() });
    });
    call.on('reconnecting', (error: any) => {
      if (error !== undefined) this.recordException(error, 'call-reconnecting');
      this.threeWayConnectionRecovering.set(true);
    });
    call.on('reconnected', () => this.threeWayConnectionRecovering.set(false));
  }

  private logThreeWay(event: string, detail: Record<string, unknown>) {
    console.info('[softphone-coach]', { at: new Date().toISOString(), event, actor: 'coach', ...detail });
  }

  async toggleMute() {
    if (this.conferenceMutationPending()) return;
    if (this.state() !== 'active') return;
    try {
      const newMuted = !this.muted();
      this.togglingMute.set(true);
      await this.activityService.coachingMute(this.transfer._id!.toString(), newMuted);
      this.muted.set(newMuted);
      this.updateStateDetail();
      this.activeCall?.mute(newMuted);
    } catch (error: any) {
      this.recordException(error, 'mute');
      this.app.message$.next({ severity: 'danger', summary: 'Coach', detail: this.getFriendlyActionError(error, 'mute') });
    } finally {
      this.togglingMute.set(false);
    }
  }

  async setCoachMode(mode: CoachMode) {
    if (this.conferenceMutationPending()) return;
    if (this.state() !== 'active' || this.coachMode() === mode) return;
    try {
      this.switchingMode.set(true);
      await this.activityService.coachingMode(this.transfer._id!.toString(), mode);
      this.coachMode.set(mode);
      this.transfer.transferMode.coachMode = mode;
      this.updateStateDetail();
    } catch (error: any) {
      this.recordException(error, 'mode');
      this.app.message$.next({ severity: 'danger', summary: 'Coach', detail: this.getFriendlyActionError(error, 'mode') });
    } finally {
      this.switchingMode.set(false);
    }
  }

  onThreeWayNumberChange(value: string) {
    this.threeWayNumber.set(value);
    if (!value?.trim()) {
      localStorage.removeItem(this.threeWayNumberStorageKey);
      return;
    }
    localStorage.setItem(this.threeWayNumberStorageKey, value);
  }

  async startThreeWayCall() {
    const readiness = {
      reason: this.threeWayReadinessReason(),
      ready: this.canStartThreeWay(),
      reconnecting: this.threeWayConnectionRecovering(),
      hasTransferContext: !!this.transfer?._id,
      callIdAvailable: !!this.getActiveCallIdForParticipantOps(),
      callSidAvailable: !!this.activeCall?.parameters?.['CallSid'],
      mutationPending: this.conferenceMutationPending(),
    };
    this.logThreeWay('three-way:submit', readiness);
    if (!readiness.ready) {
      this.logThreeWay('three-way:blocked', readiness);
      return;
    }

    const targetNumber = normalizePhone(this.threeWayNumber()).phone;
    const callSid = this.activeCall?.parameters?.['CallSid'];
    if (!targetNumber) {
      this.app.message$.next({ severity: 'warning', summary: 'Coach', detail: 'Three-way number is required.' });
      return;
    }
    if (!callSid) {
      this.app.message$.next({ severity: 'warning', summary: 'Coach', detail: 'The active Coach call is still connecting. Please try again in a moment.' });
      return;
    }
    this.placingThreeWay.set(true);
    try {
      const callId = await this.ensureActiveCallId();
      if (!callId) {
        this.logThreeWay('three-way:blocked', { ...readiness, reason: 'call-context-unavailable' });
        this.app.message$.next({ severity: 'warning', summary: 'Coach', detail: 'Call context is still loading. Please try again in a moment.' });
        return;
      }
      this.updateStateDetail();
      localStorage.setItem(this.threeWayNumberStorageKey, targetNumber);
      await this.softphoneService.threeWayCall({
        callSid,
        callId,
        targetNumber,
        activityId: this.transfer._id!.toString(),
      });
      this.threeWayAccepted.set(true);
      this.logThreeWay('three-way:accepted', { callId, callSid, activityId: this.transfer._id?.toString() });
      const refreshed = await this.refreshParticipants();
      if (refreshed) this.logThreeWay('three-way:snapshot-applied', { callId, callSid, activityId: this.transfer._id?.toString() });
    } catch (error: any) {
      this.recordException(error, 'three-way');
      this.logThreeWay('three-way:failed', { error: this.summarizeError(error) });
      this.app.message$.next({ severity: 'danger', summary: 'Coach', detail: this.getFriendlyActionError(error, 'three-way') });
    } finally {
      this.placingThreeWay.set(false);
      this.updateStateDetail();
    }
  }

  async refreshParticipants(showError = false) {
    try {
      return await this.conferenceSession.refresh();
    } catch (error: any) {
      this.recordException(error, 'participants-refresh');
      if (showError) {
        this.app.message$.next({ severity: 'warning', summary: 'Coach', detail: 'Unable to refresh participants.' });
      }
      return false;
    }
  }

  async onParticipantAction(participantCallSid: string, action: 'mute' | 'unmute' | 'hold' | 'unhold') {
    if (this.conferenceMutationPending()) return;
    const callId = await this.ensureActiveCallId();
    if (!callId || !participantCallSid) return;

    try {
      await this.conferenceSession.act(participantCallSid, action);
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
    } catch (error: any) {
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.recordException(error, `participant-${action}`);
      await this.handleConferenceMutationFailure(error, 'participant');
    }
  }

  async onMainParticipantAction(action: 'mute' | 'unmute' | 'hold' | 'unhold') {
    const callSid = this.mainParticipantLeg()?.callSid;
    if (!callSid) return;
    await this.onParticipantAction(callSid, action);
  }

  async onThirdParticipantAction(action: 'mute' | 'unmute' | 'hold' | 'unhold') {
    if (this.thirdParticipantEnded()) return;
    const callSid = this.thirdParticipantLeg()?.callSid;
    if (!callSid) return;
    await this.onParticipantAction(callSid, action);
  }

  async endParticipant(participantCallSid: string) {
    if (this.conferenceMutationPending()) return;
    const callId = await this.ensureActiveCallId();
    if (!callId || !participantCallSid) return;

    try {
      await this.conferenceSession.remove(participantCallSid);
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
    } catch (error: any) {
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.recordException(error, 'participant-end');
      await this.handleConferenceMutationFailure(error, 'participant');
    }
  }

  async endMainParticipant() {
    const callSid = this.mainParticipantLeg()?.callSid;
    if (!callSid) return;
    await this.endParticipant(callSid);
  }

  async endThirdParticipant() {
    if (this.thirdParticipantEnded()) return;
    const callSid = this.thirdParticipantLeg()?.callSid;
    if (!callSid) return;
    await this.endParticipant(callSid);
  }

  dismissEndedThirdParticipant() {
    if (this.conferenceMutationPending()) return;
    const callSid = this.thirdParticipantLeg()?.callSid;
    if (!callSid || !this.conferenceSession.dismissEndedThirdParticipant(callSid)) return;
    this.threeWayAccepted.set(false);
    this.thirdLegStartedAt.set(undefined);
    this.thirdDurationSecs.set(0);
  }

  async endConference() {
    if (this.conferenceMutationPending()) return;
    const callId = await this.ensureActiveCallId();
    if (!callId) return;
    const confirmed = await firstValueFrom(this.dialog.open(this.endConferenceConfirmation, {
      width: '420px',
      maxWidth: '95vw',
      autoFocus: false,
      restoreFocus: false,
    }).afterClosed());
    if (!confirmed || this.conferenceMutationPending()) return;

    try {
      await this.conferenceSession.endAll();
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
    } catch (error: any) {
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.recordException(error, 'conference-end');
      await this.handleConferenceMutationFailure(error, 'conference');
    }
  }

  private async handleConferenceMutationFailure(error: any, kind: 'participant' | 'conference') {
    const refreshed = await this.refreshParticipants(false);
    const message = this.getFriendlyActionError(error, kind);
    this.app.message$.next({
      severity: 'danger',
      summary: 'Coach',
      detail: refreshed
        ? message
        : `${message} Conference status could not be refreshed. Close and reopen the coaching session to try again.`,
    });
  }

  private getFriendlyActionError(error: any, kind: CoachActionKind) {
    const fallbackByKind: Record<CoachActionKind, string> = {
      join: 'Failed to join the coaching session. Please try again.',
      call: 'The coaching call ended unexpectedly. Rejoin to continue.',
      mute: 'Unable to change mute state.',
      mode: 'Failed to switch coaching mode. Please try again.',
      'three-way': 'Failed to start the three-way call. Please try again.',
      participant: 'Unable to update the participant. Please try again.',
      conference: 'Unable to end the conference. Please try again.',
    };
    const source = error?.cause || error;
    if (source?.name === 'NotAllowedError') return 'Microphone access is required to join the coaching session.';
    const status = Number(source?.status);
    const code = typeof source?.error?.code === 'string' ? source.error.code : source?.code;
    if (code === 'SOFTPHONE_PROVIDER_AUTH_FAILED') return 'Twilio credentials were rejected. Contact an admin.';
    if (status === 0) return 'The call control service could not be reached. Check your connection and try again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 409 || status === 422) return 'This action is no longer valid for the current call state.';
    if (status >= 500) return 'The call control service is temporarily unavailable. Please try again.';
    return fallbackByKind[kind];
  }

  private summarizeError(error: any) {
    this.diagnostics ||= new SoftphoneDiagnostics();
    return this.diagnostics.sanitize(error);
  }

  private recordException(error: unknown, operation: string) {
    this.exceptions.record(error, {
      source: 'coach', operation,
      transferId: this.transfer?._id?.toString(),
      callId: this.transfer?.callId?.toString()?.trim() || this.activeCallVoxrId(),
      coachCallSid: this.coachCallSid(),
      integrationId: this.transfer?.integrationId,
    });
  }

  async leaveSession() {
    if (this.conferenceMutationPending()) return;
    await this.requestLeaveIfJoined();
    this.destroyDevice();
    this.state.set('idle');
    this.stateDetail.set('Coaching session ended.');
    await this.closeSession(true);
  }

  async closeSession(force = false) {
    if (this.isClosing) return;
    if (!force) {
      if (this.hasOngoingCall()) return;
      await this.requestLeaveIfJoined();
    }

    this.isClosing = true;
    this.dialogRef?.close();
    this.left.emit();
  }

  private async requestLeaveIfJoined() {
    if (this.leaveRequested || !this.coachCallSid() || !this.transfer?._id) return;

    this.leaveRequested = true;
    try {
      await this.activityService.coachingLeave(this.transfer._id.toString());
    } catch (error) { this.recordException(error, 'leave-cleanup'); }
  }

  private resolveAndSetCallId(call?: Call) {
    if (this.activeCallVoxrId()) return;
    const callId = call?.parameters?.['CallId'] || this.transfer.callId;
    if (!callId) return;
    this.activeCallVoxrId.set(callId);
  }

  private async ensureActiveCallId() {
    const existing = this.getActiveCallIdForParticipantOps();
    if (existing) return existing;
    const callId = this.activeCall?.parameters?.['CallId'] || this.transfer.callId;
    if (!callId) return undefined;
    this.activeCallVoxrId.set(callId);
    return callId;
  }

  private getActiveCallIdForParticipantOps() {
    return this.transfer.callId?.toString()?.trim() || this.activeCallVoxrId() || undefined;
  }

  private applyConferenceTiming(rows: SoftphoneParticipantRow[]) {
    const mainStartedAt = rows.find(row => row.legType === 'main' && !!row.startedAt)?.startedAt;
    const thirdStartedAt = rows.find(row => row.legType === 'third' && !!row.startedAt && !row.endedAt)?.startedAt;
    this.mainLegStartedAt.set(mainStartedAt || this.mainLegStartedAt());
    this.thirdLegStartedAt.set(thirdStartedAt);
    this.updateStateDetail();
  }

  private startUiTimer() {
    if (this.uiTimer) return;
    this.uiTimer = setInterval(() => {
      if (this.joinedAt) {
        this.coachDurationSecs.set(Math.max(0, Math.floor((Date.now() - this.joinedAt) / 1000)));
      }
      const mainStartedAt = this.mainLegStartedAt();
      if (mainStartedAt) {
        this.mainDurationSecs.set(Math.max(0, Math.floor((Date.now() - mainStartedAt) / 1000)));
      }
      const thirdStartedAt = this.thirdLegStartedAt();
      if (thirdStartedAt) {
        this.thirdDurationSecs.set(Math.max(0, Math.floor((Date.now() - thirdStartedAt) / 1000)));
      } else {
        this.thirdDurationSecs.set(0);
      }
    }, 1000);
  }

  private stopUiTimer() {
    if (this.uiTimer) clearInterval(this.uiTimer);
    this.uiTimer = undefined;
  }

  private resetParticipantState() {
    this.conferenceSession.reset();
    this.mainLegStartedAt.set(undefined);
    this.thirdLegStartedAt.set(undefined);
    this.mainDurationSecs.set(0);
    this.thirdDurationSecs.set(0);
  }

  private reconcileParticipantSnapshot(payload?: SoftphoneParticipantSnapshotDto) {
    void this.conferenceSession.reconcile(payload).catch(error => {
      this.recordException(error, 'participants-reconcile');
      this.logThreeWay('participants:reconcile-failed', { error: this.summarizeError(error) });
    });
  }

  private formatDuration(totalSeconds?: number) {
    const secs = Math.max(0, Math.floor(totalSeconds || 0));
    const hours = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const rem = secs % 60;
    if (hours > 0) return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${rem.toString().padStart(2, '0')}`;
    return `${mins.toString().padStart(2, '0')}:${rem.toString().padStart(2, '0')}`;
  }

  private updateStateDetail() {
    if (this.placingThreeWay()) {
      this.stateDetail.set('Starting three-way call');
      return;
    }
    if (this.thirdParticipantLeg()?.callSid) {
      this.stateDetail.set('Three-way leg active');
      return;
    }
    if (this.muted()) {
      this.stateDetail.set('Listening silently');
      return;
    }
    if (this.coachMode() === 'barge') {
      this.stateDetail.set('Speaking to all participants');
      return;
    }
    this.stateDetail.set('Whispering to agent');
  }

  private destroyDevice() {
    try { this.activeCall?.disconnect(); } catch (error) { this.recordException(error, 'disconnect-cleanup'); }
    void this.releaseCoachRuntime();

    this.activeCall = undefined;
    this.threeWayConnectionRecovering.set(false);
    this.threeWayAccepted.set(false);
    this.device = undefined;
    this.consumerId = undefined;
    this.pendingIncomingCall = undefined;
    this.resetParticipantState();
  }

  private publishCoachActiveCall(call: Call) {
    const callId = this.getActiveCallIdForParticipantOps();
    const integrationId = this.transfer?.integrationId;
    const callSid = call.parameters?.['CallSid'] || this.coachCallSid();
    if (!callId || !integrationId || !callSid) return;

    const activeCall: SoftphoneSessionActiveCallSummary & { callId: string; integrationId: string; } = {
      callId,
      integrationId,
      transferId: this.transfer._id?.toString(),
      callSid,
      startedAt: Date.now(),
    };
    this.publishedActiveCall = activeCall;
    void this.softphoneService.publishActiveCall(activeCall);
  }

  private async releaseCoachRuntime() {
    const consumerId = this.consumerId;
    const activeCall = this.publishedActiveCall;
    this.consumerId = undefined;
    this.publishedActiveCall = undefined;

    if (consumerId) this.softphoneService.clearCoachIncomingSession(consumerId);
    await Promise.all([
      consumerId
        ? this.softphoneService.releaseSoftphoneDevice(consumerId, false).catch(error => {
          this.recordException(error, 'release-device-cleanup');
          throw error;
        })
        : Promise.resolve(),
      activeCall
        ? this.softphoneService.clearActiveCallIfCurrent(activeCall).catch(error => {
          this.recordException(error, 'clear-active-call-cleanup');
          throw error;
        })
        : Promise.resolve(false),
    ]);
  }
}
